import React from "react";
import { useTable, useSortBy, useGroupBy, useExpanded, usePagination, useFilters } from "react-table";
import DatePicker from "react-datepicker";
import { SvgIcon } from ".";
import { IMaskInput } from "react-imask";
import moment from "moment";
import { Controller } from "react-hook-form";
import { Pagination } from "./Pagination";

const defaultPropGetter = () => ({});

export const Table = React.forwardRef(
  (
    {
      columns,
      data,
      getHeaderProps = defaultPropGetter,
      getColumnProps = defaultPropGetter,
      getRowProps = defaultPropGetter,
      getCellProps = defaultPropGetter,
      hiddenColumns = [],
      updateData,
      skipPageReset,
      register,
      control,
      errors,
      groupBy = []
    },
    ref
  ) => {
    const instance = useTable(
      {
        columns,
        data,
        initialState: { pageIndex: 0, groupBy: groupBy },
        autoResetPage: !skipPageReset,
        updateData,
        register,
        control,
        errors
      },
      useFilters,
      useGroupBy,
      useSortBy,
      useExpanded,
      usePagination
    );
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      setHiddenColumns,
      page,
      canPreviousPage,
      canNextPage,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      rows,
      toggleAllRowsExpanded,
      pageCount,
      state: { pageSize, pageIndex }
    } = instance;

    React.useImperativeHandle(ref, () => instance);

    const [start, setStart] = React.useState(1);
    const [end, setEnd] = React.useState(data.length < pageSize ? data.length : pageSize);
    const startCount = (pageIndex + 1) * pageSize - (pageSize - 1);
    let endCount = pageCount != pageIndex + 1 ? (pageIndex + 1) * pageSize : rows.length;
    let subRowCount = 0;

    if (groupBy.length > 0) {
      subRowCount = rows
        .map((row) => row.subRows.length)
        .reduce((accumulator, currrent) => {
          return +accumulator + +currrent;
        }, 0);
      endCount = pageCount != pageIndex + 1 ? (pageIndex + 1) * pageSize : subRowCount;
    }

    React.useEffect(() => {
      setHiddenColumns([columns[0].id, ...hiddenColumns]);
      if (groupBy.length > 0) {
        toggleAllRowsExpanded(true);
      }
    }, [columns]);

    return (
      <div className="dt-bootstrap5 no-footer">
        <div className="row">
          <div className="col-sm-12 col-md-6">
            <div className="dataTables_length" id="projects-datatable_length">
              <label>
                Show{" "}
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    gotoPage(0);
                    setStart(1);
                    setEnd(data.length < Number(e.target.value) ? data.length : Number(e.target.value));
                  }}
                >
                  {[10, 25, 50, 100].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
                entries
              </label>
            </div>
          </div>
          <div className="col-sm-12 col-md-6">
            <div id="projects-datatable_filter" className="dataTables_filter"></div>
          </div>
        </div>
        <table {...getTableProps()} className="datatables table table--striped table--condensed">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    className="text-center text-bold pointer"
                    {...column.getHeaderProps([
                      {
                        className: column.className,
                        style: column.style
                      },
                      getColumnProps(column),
                      getHeaderProps(column),
                      column.getSortByToggleProps()
                    ])}
                  >
                    {column.render("Header")}
                    {"\t"}
                    {column.isSorted && (column.isSortedDesc ? <SvgIcon name="chevron-up" /> : <SvgIcon name="chevron-down" />)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps(getRowProps(row))}>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        {...cell.getCellProps([
                          {
                            className: cell.column.className,
                            style: cell.column.style
                          },
                          getColumnProps(cell.column),
                          getCellProps(cell)
                        ])}
                      >
                        {cell.isGrouped ? (
                          <>
                            <div className="d-flex" {...row.getToggleRowExpandedProps()}>
                              {row.isExpanded ? <SvgIcon name="chevron-down" className="mr-1" /> : <SvgIcon name="chevron-up" className="mr-1" />}
                              {cell.render("Cell")}
                            </div>
                          </>
                        ) : cell.isAggregated ? (
                          cell.render("Aggregated")
                        ) : cell.isPlaceholder ? null : (
                          cell.render("Cell")
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="row mt-2">
          <div className="col-sm-12 col-md-5">
            <div className="dataTables_info" aria-live="polite">
              {rows.length != 0 && rows.length == data.length
                ? `Showing ${start} to ${end} of ${data.length} entries`
                : rows.length < 1
                ? `No entries to show. (filtered from ${data.length} total entries)`
                : `Showing ${startCount} to ${endCount} of ${groupBy.length > 0 ? subRowCount : rows.length} entries (filtered from ${
                    data.length
                  } total entries)`}
            </div>
          </div>
          <div className="col-sm-12 col-md-7">
            <div className="dataTables_paginate paging_simple_numbers">
              <Pagination
                data={rows}
                pageSize={pageSize}
                pageNeighbours={1}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                setStart={setStart}
                setEnd={setEnd}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export const EditableInputCell = ({ value: initialValue, row: { index }, column: { id }, updateData, register, errors }) => {
  const [value, setValue] = React.useState(initialValue || "");

  const onChange = (e) => {
    setValue(e.target.value);
  };

  const onBlur = () => {
    updateData(index, id, value);
  };

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <input
      {...register(`data.${index}.${id}`)}
      className="field"
      onChange={onChange}
      onBlur={onBlur}
      style={errors?.["data"]?.[index]?.[id]?.message ? { borderColor: "red" } : {}}
    />
  );
};

export const EditableMaskedInputCell = ({ value: initialValue, row: { index }, column: { id }, updateData, control, errors }) => {
  const [value, setValue] = React.useState(initialValue || "");

  const onChange = (e) => {
    setValue(e);
  };

  const onBlur = () => {
    updateData(index, id, value);
  };

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <Controller
      name={`data.${index}.${id}`}
      control={control}
      render={({ field }) => (
        <IMaskInput
          mask={Number}
          padFractionalZeros={true}
          thousandsSeparator=","
          radix="."
          unmask={true}
          onAccept={(value) => {
            onChange(value);
            field.onChange(value);
          }}
          value={field.value?.toString()}
          inputRef={field.ref}
          onBlur={onBlur}
          className="field"
          placeholder="0.00"
          min={0}
          max={9999999}
          style={errors?.["data"]?.[index]?.[id]?.message ? { borderColor: "red" } : {}}
        />
      )}
    />
  );
};

export const EditableDateCell = ({ value: initialValue, row: { index }, column: { id }, updateData, control, errors }) => {
  const [value, setValue] = React.useState(initialValue);
  const minDate = moment().toDate();

  const onChange = (date) => {
    setValue(date);
  };

  const onBlur = () => {
    updateData(index, id, value);
  };

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <Controller
      name={`data.${index}.${id}`}
      control={control}
      render={({ field }) => (
        <div className={`${errors?.["data"]?.[index]?.[id]?.message ? "field-group--invalid" : ""}`}>
          <DatePicker
            selected={value}
            onChange={(date) => {
              field.onChange(date);
              onChange(date);
            }}
            onBlur={(e) => {
              field.onBlur(e);
              onBlur();
            }}
            className="field"
            minDate={minDate}
            placeholderText="Select Date"
          />
        </div>
      )}
    />
  );
};
